import {
  type DataProvider as RaDataProvider,
  useDataProvider
} from 'react-admin'
import simpleRestProvider from 'ra-data-simple-rest'
import log, { type LogLevelDesc } from 'loglevel'
import { useQuery } from 'react-query'

import {
  fetchJson,
  getAnnounces,
  getConfig,
  getElectricity,
  getElectricityBySpin,
  getElectricityDemand,
  getElectricityTargets,
  getHolidays,
  url
} from './functions'
import {
  type ElectricityYearly,
  type ElectricityYearlyData,
  type ElectricityDaily,
  type ElectricityDailyData,
  type ElectricityMonthly,
  type ElectricityMonthlyData,
  type ElectricityWeekly,
  type ElectricityWeeklyData,
  type ElectricityChartDailyKind,
  type ElectricityChartMonthlyKind,
  type ElectricityChartYearlyKind,
  type ElectricityAllYearlyData,
  type AnnounceItem,
  type ElectricityDemandData,
  type ElectricityDailyTargetsData,
  type ElectricityYearlyTargetsData,
  type ElectricityMonthlyTargetsData
} from '../../../../cdk/bapp/lib/lambda-api/src/types'
import { compact, filter, includes, isNil, map, size } from 'lodash/fp'

log.setLevel((process.env['LOG_LEVEL'] as LogLevelDesc) ?? log.levels.DEBUG)

export const DataProvider = (partner: string | undefined): RaDataProvider => ({
  ...simpleRestProvider(url as string, fetchJson(partner)),
  getElectricityDaily: async (
    date: string,
    compareTo: ElectricityChartDailyKind
  ) => {
    return await getElectricity<ElectricityDaily>(
      partner,
      'daily',
      date,
      compareTo
    )
  },
  getElectricityDailyBySpin: async (
    date: string,
    param: {
      spin: string
      compareTo: ElectricityChartDailyKind
      demand: boolean
    }
  ) => {
    const { spin, compareTo, demand } = param
    return await getElectricityBySpin<ElectricityDaily>(
      partner,
      spin,
      'daily',
      date,
      compareTo,
      demand
    )
  },
  getElectricityDailyTargets: async (
    date: string,
    param: {
      compareTo: ElectricityChartDailyKind
      targetKeys: string[]
      targetValues: string[]
    }
  ) => {
    const { compareTo, targetKeys, targetValues } = param
    return await getElectricityTargets<ElectricityDailyTargetsData>(
      partner,
      'daily',
      date,
      compareTo,
      targetKeys,
      targetValues
    )
  },
  getElectricityWeekly: async (date: string) =>
    await getElectricity<ElectricityWeekly>(partner, 'weekly', date),
  getElectricityWeeklyBySpin: async (date: string, param: { spin: string }) => {
    const { spin } = param
    return await getElectricityBySpin<ElectricityWeekly>(
      partner,
      'weekly',
      date,
      spin
    )
  },
  getElectricityMonthly: async (
    date: string,
    compareTo: ElectricityChartMonthlyKind
  ): Promise<ElectricityMonthly | undefined> =>
    await getElectricity<ElectricityMonthly>(partner, 'month', date, compareTo),
  getElectricityMonthlyBySpin: async (
    date: string,
    param: {
      spin: string
      compareTo: ElectricityChartMonthlyKind
      demand: boolean
    }
  ): Promise<ElectricityMonthly | undefined> => {
    const { spin, compareTo, demand } = param
    return await getElectricityBySpin<ElectricityMonthly>(
      partner,
      spin,
      'month',
      date,
      compareTo,
      demand
    )
  },
  getElectricityMonthlyTargets: async (
    date: string,
    param: {
      compareTo: ElectricityChartMonthlyKind
      targetKeys: string[]
      targetValues: string[]
    }
  ) => {
    const { compareTo, targetKeys, targetValues } = param
    return await getElectricityTargets<ElectricityMonthlyTargetsData>(
      partner,
      'month',
      date,
      compareTo,
      targetKeys,
      targetValues
    )
  },
  getElectricityMonthlyAverageBySpin: async (
    date: string,
    param: { spin: string }
  ): Promise<ElectricityYearly | undefined> => {
    const { spin } = param
    return await getElectricityBySpin<ElectricityYearly>(
      partner,
      spin,
      'yearAgerage',
      date
    )
  },
  getElectricityYearly: async (
    date: string,
    compareTo: ElectricityChartYearlyKind
  ): Promise<ElectricityYearly | undefined> =>
    await getElectricity<ElectricityYearly>(partner, 'year', date, compareTo),
  getElectricityYearlyBySpin: async (
    date: string,
    param: {
      spin: string
      compareTo: ElectricityChartYearlyKind
      demand: boolean
    }
  ): Promise<ElectricityYearly | undefined> => {
    const { spin, compareTo, demand } = param
    return await getElectricityBySpin<ElectricityYearly>(
      partner,
      spin,
      'year',
      date,
      compareTo,
      demand
    )
  },
  getElectricityYearlyTargets: async (
    date: string,
    param: {
      compareTo: ElectricityChartYearlyKind
      targetKeys: string[]
      targetValues: string[]
    }
  ) => {
    const { compareTo, targetKeys, targetValues } = param
    return await getElectricityTargets<ElectricityYearlyTargetsData>(
      partner,
      'year',
      date,
      compareTo,
      targetKeys,
      targetValues
    )
  },
  getElectricityManyYearlyBySpin: async (
    date: string,
    param: { spins: string[] }
  ): Promise<Array<{ data: ElectricityYearly | undefined }>> => {
    const { spins } = param
    if (size(spins) < 5) {
      const data = compact(
        await Promise.all(
          map(
            async (spin: string) =>
              await getElectricityBySpin<{ data: ElectricityYearly }>(
                partner,
                spin,
                'year',
                date
              )
          )(spins)
        )
      )
      return data
    } else {
      const all = await getElectricity<ElectricityAllYearlyData>(
        partner,
        'year',
        date
      )
      if (isNil(all)) {
        return []
      }
      return map((data: ElectricityYearly) => ({ data }))(
        filter((item: ElectricityYearly) => includes(item.spin)(spins))(
          all.data
        )
      )
    }
  },
  getHolidays: async (
    year: number
  ): Promise<Record<string, string> | undefined> => await getHolidays(year),
  getConfig: async (
    partner: string
  ): Promise<Record<string, any> | undefined> => await getConfig(partner),
  getAnnounces: async (partner: string): Promise<AnnounceItem[] | undefined> =>
    await getAnnounces(partner),
  getElectricityDemand: async (
    date: string,
    param: {
      spin: string
    }
  ) => {
    const { spin } = param
    return await getElectricityDemand(partner, spin, date)
  }
})
export const useGetElectricityDaily = (
  date: string,
  compareTo: ElectricityChartDailyKind
): { isLoading: boolean; data: ElectricityDailyData | undefined } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['electricityDaily', date, compareTo],
    queryFn: () => dataProvider['getElectricityDaily'](date, compareTo)
  })
  return { isLoading, data }
}
export const useGetElectricityDailyBySpin = (
  date: string,
  spin: string,
  compareTo: ElectricityChartDailyKind,
  demand: boolean = false
): { isLoading: boolean; data: ElectricityDailyData | undefined } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['electricityDaily', date, spin, compareTo, demand],
    queryFn: () =>
      dataProvider['getElectricityDailyBySpin'](date, {
        spin,
        compareTo,
        demand
      }),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetElectricityWeekly = (
  date: string
): { isLoading: boolean; data: ElectricityWeeklyData | undefined } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['electricityWeekly', date],
    queryFn: () => dataProvider['getElectricityWeekly'](date),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetElectricityWeeklyBySpin = (
  date: string,
  spin: string
): { isLoading: boolean; data: ElectricityWeeklyData | undefined } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['electricityWeekly', date, spin],
    queryFn: () => dataProvider['getElectricityWeeklyBySpin'](date, { spin }),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetElectricityMonthly = (
  date: string,
  compareTo: ElectricityChartMonthlyKind
): { isLoading: boolean; data: ElectricityMonthlyData | undefined } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['electricityMonthly', date, compareTo],
    queryFn: () => dataProvider['getElectricityMonthly'](date, compareTo),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetElectricityMonthlyBySpin = (
  date: string,
  spin: string,
  compareTo: ElectricityChartMonthlyKind,
  demand: boolean = false
): { isLoading: boolean; data: ElectricityMonthlyData | null | undefined } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['electricityMonthly', date, spin, compareTo, demand],
    queryFn: () =>
      dataProvider['getElectricityMonthlyBySpin'](date, {
        spin,
        compareTo,
        demand
      }),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetElectricityMonthlyAverageBySpin = (
  date: string,
  spin: string
): { isLoading: boolean; data: ElectricityYearlyData | undefined } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['electricityMonthlyAverage', date, spin],
    queryFn: () =>
      dataProvider['getElectricityMonthlyAverageBySpin'](date, { spin }),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetElectricityYearly = (
  date: string,
  compareTo: ElectricityChartYearlyKind
): { isLoading: boolean; data: ElectricityYearlyData | undefined } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['electricityYearly', date, compareTo],
    queryFn: () => dataProvider['getElectricityYearly'](date, compareTo),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetElectricityYearlyBySpin = (
  date: string,
  spin: string,
  compareTo: ElectricityChartYearlyKind,
  demand: boolean
): { isLoading: boolean; data: ElectricityYearlyData | undefined } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['electricityYearly', date, spin, compareTo, demand],
    queryFn: () =>
      dataProvider['getElectricityYearlyBySpin'](date, {
        spin,
        compareTo,
        demand
      }),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetElectricityManyYearlyBySpin = (
  date: string,
  spins: string[]
): { isLoading: boolean; data: ElectricityYearlyData[] } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: [
      'electricityYearly',
      date,
      size(spins) < 5 ? JSON.stringify(spins) : 'all'
    ],
    queryFn: () =>
      dataProvider['getElectricityManyYearlyBySpin'](date, { spins }),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetHolidays = (
  year: number
): { isLoading: boolean; data: Record<string, string> | undefined } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['holidays'],
    queryFn: () => dataProvider['getHolidays'](year),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetConfig = (
  partner: string
): { isLoading: boolean; data: Record<string, any> | undefined } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['config', partner],
    queryFn: () => dataProvider['getConfig'](partner),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetAnnounces = (
  partner: string
): { isLoading: boolean; data: AnnounceItem[] } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['announces', partner],
    queryFn: () => dataProvider['getAnnounces'](partner),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetElectricityDemand = (
  date: string,
  spin: string
): { isLoading: boolean; data: ElectricityDemandData | undefined } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: ['electricityDemand', date, spin],
    queryFn: () =>
      dataProvider['getElectricityDemand'](date, {
        spin
      }),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetElectricityDailyTargets = (
  date: string,
  compareTo: ElectricityChartDailyKind,
  targetKeys?: string[],
  targetValues?: string[]
): { isLoading: boolean; data: ElectricityDailyTargetsData | undefined } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: [
      'electricityDaily',
      date,
      compareTo,
      JSON.stringify(targetKeys ?? []),
      JSON.stringify(targetValues ?? [])
    ],
    queryFn: () =>
      dataProvider['getElectricityDailyTargets'](date, {
        compareTo,
        targetKeys,
        targetValues
      }),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetElectricityMonthlyTargets = (
  date: string,
  compareTo: ElectricityChartMonthlyKind,
  targetKeys?: string[],
  targetValues?: string[]
): { isLoading: boolean; data: ElectricityMonthlyTargetsData | undefined } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: [
      'electricityMonthly',
      date,
      compareTo,
      JSON.stringify(targetKeys ?? []),
      JSON.stringify(targetValues ?? [])
    ],
    queryFn: () =>
      dataProvider['getElectricityMonthlyTargets'](date, {
        compareTo,
        targetKeys,
        targetValues
      }),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
export const useGetElectricityYearlyTargets = (
  date: string,
  compareTo: ElectricityChartYearlyKind,
  targetKeys?: string[],
  targetValues?: string[]
): { isLoading: boolean; data: ElectricityYearlyTargetsData | undefined } => {
  const dataProvider = useDataProvider()
  const { data, isLoading } = useQuery({
    queryKey: [
      'electricityYearly',
      date,
      compareTo,
      JSON.stringify(targetKeys ?? []),
      JSON.stringify(targetValues ?? [])
    ],
    queryFn: () =>
      dataProvider['getElectricityYearlyTargets'](date, {
        compareTo,
        targetKeys,
        targetValues
      }),
    staleTime: isNil(process.env['NO_CACHE']) ? 3600 * 1000 : 0
  })
  return { isLoading, data }
}
